import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2 } from 'lucide-react';
import { Footer } from '../components/Footer';
import { SubpageNav } from '../components/SubpageNav';
import { SubpageCTA } from '../components/SubpageCTA';
import { fadeUp } from '../utils/animations';
import { symptomsItems, needsItems, consequences, credibilityPairs, roles, rolesHumor, workPrinciples, results, resultsHumor, ctaPersonas } from '../data/omrade3-data';
export function Omrade3() {
  return <div className="min-h-screen bg-background text-ink font-sans" data-id="element-580">
      <SubpageNav activePage="omrade-3" data-id="element-581" />

      <main className="pt-20" data-id="element-582">
        {/* ── HERO ── */}
        <section className="py-20 md:py-28 px-6 bg-background" data-id="element-583">
          <div className="max-w-4xl mx-auto" data-id="element-584">
            <motion.span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-4 block" custom={0} initial="hidden" animate="visible" variants={fadeUp} data-id="element-585">
              Område 03
            </motion.span>
            <motion.h1 className="text-4xl md:text-6xl font-light text-ink leading-[1.1] mb-6" custom={1} initial="hidden" animate="visible" variants={fadeUp} data-id="element-586">
              Hybridarbete –{' '}
              <span className="text-terracotta" data-id="element-587">infört men inte designat</span>
            </motion.h1>
            <motion.p className="text-xl text-muted font-light max-w-2xl leading-relaxed mb-8" custom={2} initial="hidden" animate="visible" variants={fadeUp} data-id="element-588">
              Ni har bestämt var ni ska arbeta. Inte hur arbetet faktiskt ska
              fungera.
            </motion.p>
            <motion.div className="w-16 h-1 bg-terracotta rounded-full" custom={3} initial="hidden" animate="visible" variants={fadeUp} data-id="element-589" />
          </div>
        </section>

        {/* ── PROBLEM ── */}
        <section className="py-16 md:py-24 px-6 bg-card/40" data-id="element-590">
          <div className="max-w-4xl mx-auto" data-id="element-591">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-592">
              <span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-593">
                01 — Problemet
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-6" data-id="element-594">
                Det digitala mötet är en kopia av det fysiska.
                <br data-id="element-595" />
                <span className="text-muted" data-id="element-596">Det fungerar inte.</span>
              </h2>
              <p className="text-lg text-muted leading-relaxed max-w-2xl" data-id="element-597">
                Många företag har infört hybridarbete — men inte designat det.
                Resultatet är friktion som kostar tid, engagemang och lönsamhet.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-598">
              <motion.div custom={1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-background rounded-2xl p-8 border border-border/50" data-id="element-599">
                <h3 className="text-sm font-medium tracking-widest uppercase text-terracotta mb-6" data-id="element-600">
                  Det ser ut såhär
                </h3>
                <ul className="space-y-3" data-id="element-601">
                  {symptomsItems.map((item, i) => <li key={i} className="flex items-start gap-3 text-muted" data-id="element-602">
                      <span className="w-1.5 h-1.5 rounded-full bg-terracotta mt-2 flex-shrink-0" data-id="element-603" />
                      <span className="text-sm leading-relaxed" data-id="element-604">{item}</span>
                    </li>)}
                </ul>
              </motion.div>

              <motion.div custom={2} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-background rounded-2xl p-8 border border-border/50" data-id="element-605">
                <h3 className="text-sm font-medium tracking-widest uppercase text-sage mb-6" data-id="element-606">
                  Det som saknas
                </h3>
                <ul className="space-y-3" data-id="element-607">
                  {needsItems.map((item, i) => <li key={i} className="flex items-start gap-3 text-muted" data-id="element-608">
                      <span className="w-1.5 h-1.5 rounded-full bg-sage mt-2 flex-shrink-0" data-id="element-609" />
                      <span className="text-sm leading-relaxed" data-id="element-610">{item}</span>
                    </li>)}
                </ul>
              </motion.div>
            </div>

            <motion.div custom={3} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 bg-ink rounded-2xl px-8 py-7" data-id="element-611">
              <p className="text-white/90 text-lg leading-relaxed" data-id="element-612">
                Hybridarbete ger frihet.{' '}
                <span className="text-white font-medium" data-id="element-613">
                  Men utan design skapas friktion.
                </span>{' '}
                <span className="text-white/60" data-id="element-614">
                  Och friktion kostar — i tid, engagemang och lönsamhet. Varje
                  ineffektivt möte är en direkt lönekostnad utan motsvarande
                  värde.
                </span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── KONSEKVENSER ── */}
        <section className="py-16 md:py-24 px-6 bg-background" data-id="element-615">
          <div className="max-w-4xl mx-auto" data-id="element-616">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-617">
              <span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-618">
                02 — Konsekvenser
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-4" data-id="element-619">
                Det som skulle ge flexibilitet
              </h2>
              <p className="text-muted text-lg" data-id="element-620">
                riskerar istället att sänka produktiviteten.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-621">
              {consequences.map((item, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="bg-card rounded-2xl p-6 border border-border/50" data-id="element-622">
                  <item.icon className="w-5 h-5 text-terracotta mb-4" strokeWidth={1.5} data-id="element-623" />
                  <p className="text-ink font-medium text-sm mb-1" data-id="element-624">
                    {item.text}
                  </p>
                  <p className="text-muted text-sm" data-id="element-625">{item.sub}</p>
                </motion.div>)}
            </div>

            <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 text-center" data-id="element-626">
              <p className="text-xl text-ink font-light" data-id="element-627">
                Problemet är inte hybridmodellen.
              </p>
              <p className="text-muted mt-2" data-id="element-628">Det är arbetssättet.</p>
            </motion.div>
          </div>
        </section>

        {/* ── TROVÄRDIGHET ── */}
        <section className="py-16 md:py-24 px-6 bg-ink text-white" data-id="element-629">
          <div className="max-w-4xl mx-auto" data-id="element-630">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-631">
              <span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-632">
                Perspektiv
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-white mb-4" data-id="element-633">
                Hybrid kräver medveten design.
              </h2>
              <p className="text-white/70 text-lg max-w-2xl leading-relaxed" data-id="element-634">
                Jag har designat och faciliterat arbete i hybridmiljöer där det
                verkligen gäller. Inte som policy — utan som praktik. Det
                handlar om att bygga in rätt struktur i hur arbetet faktiskt
                sker.
              </p>
            </motion.div>

            <div className="space-y-3" data-id="element-635">
              {credibilityPairs.map((pair, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="flex items-center gap-4 bg-white/5 rounded-xl px-6 py-4 border border-white/10" data-id="element-636">
                  <span className="text-white font-medium text-sm flex-1 text-right" data-id="element-637">
                    {pair.left}
                  </span>
                  <span className="text-terracotta text-xs font-medium tracking-widest uppercase px-3" data-id="element-638">
                    →
                  </span>
                  <span className="text-white/70 text-sm flex-1" data-id="element-639">
                    {pair.right}
                  </span>
                </motion.div>)}
            </div>

            <motion.p custom={6} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-10 text-white/50 text-sm italic" data-id="element-640">
              Hybrid ska höja prestation, tempo och lönsamhet — inte sänka dem.
            </motion.p>
          </div>
        </section>

        {/* ── LÖSNING ── */}
        <section className="py-16 md:py-24 px-6 bg-background" data-id="element-641">
          <div className="max-w-4xl mx-auto" data-id="element-642">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-643">
              <span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-644">
                03 — Lösningen
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink mb-4" data-id="element-645">
                1 dag i veckan.{' '}
                <span className="text-terracotta" data-id="element-646">
                  Vi designar arbetssättet.
                </span>
              </h2>
              <p className="text-muted text-lg max-w-2xl leading-relaxed" data-id="element-647">
                Inte bara policyn. Jag kliver in operativt och arbetar med
                mötesdesign, facilitering, digital kommunikation och hållbara
                hybridrutiner.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4" data-id="element-648">
              {roles.map((role, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="flex items-start gap-4 bg-card rounded-xl p-5 border border-border/50" data-id="element-649">
                  <div className="w-8 h-8 rounded-full bg-terracotta/10 flex items-center justify-center flex-shrink-0" data-id="element-650">
                    <role.icon className="w-4 h-4 text-terracotta" strokeWidth={1.5} data-id="element-651" />
                  </div>
                  <span className="text-sm text-ink leading-relaxed" data-id="element-652">
                    {role.label}
                  </span>
                </motion.div>)}
            </div>

            <motion.p custom={6} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="text-xs text-muted italic text-center pt-5 border-t border-border/30 mb-14" data-id="element-653">
              * {rolesHumor}
            </motion.p>

            <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="bg-card/60 rounded-2xl p-8 border border-border/50" data-id="element-654">
              <h3 className="text-sm font-medium tracking-widest uppercase text-ink mb-6" data-id="element-655">
                Hur vi arbetar
              </h3>
              <ul className="space-y-4" data-id="element-656">
                {workPrinciples.map((p, i) => <li key={i} className="flex items-start gap-3" data-id="element-657">
                    <CheckCircle2 className="w-4 h-4 text-sage mt-0.5 flex-shrink-0" strokeWidth={1.5} data-id="element-658" />
                    <span className="text-muted text-sm leading-relaxed" data-id="element-659">
                      {p}
                    </span>
                  </li>)}
              </ul>
              <p className="mt-8 text-ink font-medium text-sm border-t border-border/50 pt-6" data-id="element-660">
                Målet är enkelt:{' '}
                <span className="text-terracotta" data-id="element-661">
                  Hybridarbete ska höja prestation, tempo och lönsamhet — inte
                  sänka dem.
                </span>
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── RESULTAT ── */}
        <section className="py-16 md:py-24 px-6 bg-card/40" data-id="element-662">
          <div className="max-w-4xl mx-auto" data-id="element-663">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mb-12" data-id="element-664">
              <span className="text-terracotta font-medium tracking-widest text-xs uppercase mb-3 block" data-id="element-665">
                04 — Resultatet
              </span>
              <h2 className="text-3xl md:text-4xl font-light text-ink" data-id="element-666">
                När hybrid är designat händer det här
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-667">
              {results.map((result, i) => <motion.div key={i} custom={i + 1} initial="hidden" whileInView="visible" viewport={{
              once: true
            }} variants={fadeUp} className="flex items-start gap-3 bg-background rounded-xl p-5 border border-border/50" data-id="element-668">
                  <CheckCircle2 className="w-5 h-5 text-sage mt-0.5 flex-shrink-0" strokeWidth={1.5} data-id="element-669" />
                  <span className="text-ink text-sm leading-relaxed" data-id="element-670">
                    {result}
                  </span>
                </motion.div>)}
            </div>

            <motion.p custom={6} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="text-xs text-muted italic text-center pt-5 mt-4 border-t border-border/30" data-id="element-671">
              * {resultsHumor}
            </motion.p>

            <motion.div custom={7} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeUp} className="mt-12 text-center" data-id="element-672">
              <p className="text-xl text-ink font-light" data-id="element-673">
                Hybridarbete är inte en HR-fråga.
              </p>
              <p className="text-muted mt-2" data-id="element-674">
                Det är en prestations- och lönsamhetsfråga.
              </p>
            </motion.div>
          </div>
        </section>

        {/* ── CTA ── */}
        <SubpageCTA badgeText="1 dag / vecka" badgeColor="terracotta" heading="Känner du igen dig?" subheading="Oavsett vilken roll du har — hybridarbete utan design skapar samma friktion överallt." personas={ctaPersonas} ctaText="Boka ett samtal – helt utan förpliktelser." ctaSubtext="Vi pratar om din situation, inte om mina PowerPoints." backLinkText="Tillbaka till alla områden" backLinkUrl="/" data-id="element-675" />
      </main>
      <Footer data-id="element-676" />
    </div>;
}